import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Science, Send } from '@mui/icons-material';

import { CreditRiskAPI } from '../services/api';
import { UnderwritingChatTurn } from '../types';
import ChatMarkdown from './ChatMarkdown';

const STARTERS = [
  '¿Qué cohortes usó la calibración isotónica de cada modelo?',
  '¿Qué segmentos tienen mayor tasa de default?',
  '¿Dónde se concentran los defaults: por garantía o por provincia?',
  '¿Por qué el DTI no entra al puntaje? Literatura y evidencia en esta cartera.',
  'Compara ingreso y monto de crédito entre buenos pagadores y default.',
];

interface ExpertTabProps {
  onNotification: (message: string, severity: 'success' | 'error' | 'warning' | 'info') => void;
}

const ExpertTab: React.FC<ExpertTabProps> = ({ onNotification }) => {
  const [draft, setDraft] = useState('');
  const [messages, setMessages] = useState<UnderwritingChatTurn[]>([]);
  const [sources, setSources] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const bottomRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  const send = async (text: string) => {
    const content = text.trim();
    if (!content || loading) return;

    const history = messages;
    const userTurn: UnderwritingChatTurn = { role: 'user', content };
    setMessages([...history, userTurn]);
    setDraft('');
    setLoading(true);
    setError(null);

    const response = await CreditRiskAPI.expertChat({
      message: content,
      history,
    });

    setLoading(false);
    if (!response.success || !response.data) {
      const message = response.error?.message || 'No se pudo consultar el laboratorio experto.';
      setError(message);
      onNotification(message, 'error');
      return;
    }

    setSources(response.data.sources || []);
    setMessages([
      ...history,
      userTurn,
      { role: 'assistant', content: response.data.reply },
    ]);
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Laboratorio experto
      </Typography>
      <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 2 }}>
        Metodología, literatura, stack y consultas agregadas sobre la cartera de entrenamiento
        y el extracto Core. No devuelve filas de clientes.
      </Typography>

      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
        {STARTERS.map((prompt) => (
          <Chip
            key={prompt}
            label={prompt}
            variant="outlined"
            onClick={() => send(prompt)}
            disabled={loading}
            sx={{ maxWidth: '100%', height: 'auto', py: 0.75, '& .MuiChip-label': { whiteSpace: 'normal' } }}
          />
        ))}
      </Stack>

      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" color="primary" gutterBottom>
          <Science sx={{ mr: 1, verticalAlign: 'middle' }} />
          Pregunta al modelo y a los datos
        </Typography>

        <Box
          sx={{
            height: 480,
            overflowY: 'auto',
            bgcolor: '#fafafa',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            p: 2,
            mb: 2,
          }}
        >
          {messages.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              Ejemplos: cohortes de la isotónica, lift por segmento, concentración de default,
              Siddiqi / calibración, o el split temporal del Core.
            </Typography>
          )}
          {messages.map((turn, index) => {
            const mine = turn.role === 'user';
            return (
              <Box
                key={`${turn.role}-${index}`}
                sx={{
                  display: 'flex',
                  justifyContent: mine ? 'flex-end' : 'flex-start',
                  mb: 1.5,
                }}
              >
                <Box
                  sx={{
                    maxWidth: '90%',
                    px: 1.5,
                    py: 1,
                    borderRadius: 2,
                    bgcolor: mine ? 'primary.main' : 'white',
                    color: mine ? 'white' : 'text.primary',
                    border: mine ? 'none' : '1px solid',
                    borderColor: 'divider',
                    overflowX: 'auto',
                  }}
                >
                  <Typography variant="caption" sx={{ opacity: 0.75, display: 'block', mb: 0.5 }}>
                    {mine ? 'SUMA' : 'Experto'}
                  </Typography>
                  {mine ? (
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {turn.content}
                    </Typography>
                  ) : (
                    <ChatMarkdown text={turn.content} />
                  )}
                </Box>
              </Box>
            );
          })}
          {loading && (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ color: 'text.secondary' }}>
              <CircularProgress size={16} />
              <Typography variant="caption">Consultando documentación y agregados…</Typography>
            </Stack>
          )}
          <div ref={bottomRef} />
        </Box>

        {sources.length > 0 && (
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
            {sources.map((source) => (
              <Chip key={source} size="small" label={source} variant="outlined" />
            ))}
          </Stack>
        )}

        {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}

        <Stack direction="row" spacing={1}>
          <TextField
            fullWidth
            size="small"
            placeholder="Pregunta sobre entrenamiento, calibración, literatura o la cartera…"
            value={draft}
            disabled={loading}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault();
                send(draft);
              }
            }}
          />
          <IconButton color="primary" onClick={() => send(draft)} disabled={loading || !draft.trim()}>
            <Send />
          </IconButton>
        </Stack>
      </Paper>
    </Box>
  );
};

export default ExpertTab;
